/**
 * Payment Testing Module for Admin
 * Tests all payment flows with both Stripe and PlugnPay
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  Loader2,
  CreditCard,
  DollarSign,
  Home,
  Building2,
  FileText,
  Wallet,
  PlayCircle,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentTestingModule = () => {
  const [testing, setTesting] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [liveTesting, setLiveTesting] = useState(false);
  const [liveTestResult, setLiveTestResult] = useState(null);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [testResults, setTestResults] = useState({
    stripe: {
      basic: null,
      rent: null,
      hoa_dues: null,
      invoice: null,
      wallet: null
    },
    plugnpay: {
      basic: null,
      rent: null,
      hoa_dues: null,
      invoice: null,
      wallet: null
    }
  });

  const testPaymentFlow = async (processor, flowType) => {
    const token = localStorage.getItem('access_token');
    
    try {
      const response = await axios.post(
        `${API}/admin/test-payment`,
        {
          processor: processor,
          flow_type: flowType,
          amount: 1.00
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      return {
        success: true,
        message: response.data.message,
        session_id: response.data.session_id,
        payment_url: response.data.payment_url
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.detail || error.message
      };
    }
  };

  const runAllTests = async (processor) => {
    setTesting(true);
    const flows = ['basic', 'rent', 'hoa_dues', 'invoice', 'wallet'];
    
    toast.info(`Starting ${processor.toUpperCase()} payment tests...`);

    for (const flow of flows) {
      // Update UI to show testing
      setTestResults(prev => ({
        ...prev,
        [processor]: {
          ...prev[processor],
          [flow]: 'testing'
        }
      }));

      // Run test
      const result = await testPaymentFlow(processor, flow);
      
      // Update result
      setTestResults(prev => ({
        ...prev,
        [processor]: {
          ...prev[processor],
          [flow]: result
        }
      }));

      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setTesting(false);
    toast.success(`${processor.toUpperCase()} tests completed!`);
  };

  const resetTests = () => {
    setTestResults({
      stripe: {
        basic: null,
        rent: null,
        hoa_dues: null,
        invoice: null,
        wallet: null
      },
      plugnpay: {
        basic: null,
        rent: null,
        hoa_dues: null,
        invoice: null,
        wallet: null
      }
    });
  };

  const getStatusIcon = (result) => {
    if (result === null) {
      return <div className="h-5 w-5 rounded-full bg-gray-200" />;
    }
    if (result === 'testing') {
      return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    }
    if (result.success) {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    }
    return <XCircle className="h-5 w-5 text-red-600" />;
  };

  const getStatusBadge = (result) => {
    if (result === null) {
      return <Badge variant="outline">Not Tested</Badge>;
    }
    if (result === 'testing') {
      return <Badge className="bg-blue-100 text-blue-800">Testing...</Badge>;
    }
    if (result.success) {
      return <Badge className="bg-green-100 text-green-800">✓ Passed</Badge>;
    }
    return <Badge className="bg-red-100 text-red-800">✗ Failed</Badge>;
  };

  const testFlows = [
    { id: 'basic', icon: DollarSign, label: 'Basic $1 Payment', description: 'Simple payment processing test' },
    { id: 'rent', icon: Home, label: 'Tenant Rent Payment', description: 'Simulate tenant paying monthly rent' },
    { id: 'hoa_dues', icon: Building2, label: 'HOA Dues Payment', description: 'Simulate HOA owner paying dues' },
    { id: 'invoice', icon: FileText, label: 'Invoice Payment', description: 'Pay outstanding invoice' },
    { id: 'wallet', icon: Wallet, label: 'Wallet Top-up', description: 'Add funds to wallet' }
  ];

  const getProcessorSummary = (processor) => {
    const results = testResults[processor];
    const total = Object.keys(results).length;
    const tested = Object.values(results).filter(r => r !== null && r !== 'testing').length;
    const passed = Object.values(results).filter(r => r && r.success).length;
    const failed = Object.values(results).filter(r => r && !r.success).length;

    return { total, tested, passed, failed };
  };

  const runLiveModeTest = async (processor, amount = 1.00) => {
    // Validate card details
    if (!cardDetails.cardNumber || !cardDetails.cardName || !cardDetails.expiryMonth || !cardDetails.expiryYear || !cardDetails.cvv) {
      toast.error('Please enter all card details');
      return;
    }

    setLiveTesting(true);
    setLiveTestResult(null);
    const token = localStorage.getItem('access_token');
    
    toast.info(`Running LIVE MODE test with ${processor} for $${amount.toFixed(2)}...`);
    
    try {
      const response = await axios.post(
        `${API}/admin/live-payment-test`,
        {
          processor: processor,
          amount: amount,
          test_mode: false,
          card_details: {
            card_number: cardDetails.cardNumber.replace(/\s/g, ''),
            card_name: cardDetails.cardName,
            card_exp: `${cardDetails.expiryMonth}${cardDetails.expiryYear}`,
            card_cvv: cardDetails.cvv
          }
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setLiveTestResult({
        success: true,
        processor: processor,
        message: response.data.message,
        order_id: response.data.order_id,
        session_id: response.data.session_id,
        payment_url: response.data.payment_url,
        amount: response.data.amount
      });
      
      toast.success('Live test transaction created! Check your payment processor dashboard.');
    } catch (error) {
      setLiveTestResult({
        success: false,
        processor: processor,
        message: error.response?.data?.detail || error.message
      });
      toast.error('Live test failed: ' + (error.response?.data?.detail || error.message));
    }
    
    setLiveTesting(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payment System Testing</h2>
          <p className="text-gray-600 mt-1">
            Test all payment flows with $1 transactions before going live
          </p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={resetTests}
            disabled={testing}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Tests
          </Button>
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertDescription className="text-yellow-800">
          <strong>Pre-Launch Testing:</strong> Each test creates a $1 transaction. 
          Tests verify payment session creation and routing - you don't need to complete the actual payment. 
          Check that each flow generates a valid payment URL.
        </AlertDescription>
      </Alert>

      {/* Live Mode Testing Section */}
      <Card className="border-red-300 bg-red-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-red-900 flex items-center space-x-2">
                <span>🔴</span>
                <span>LIVE MODE TESTING</span>
              </CardTitle>
              <p className="text-red-700 mt-2 text-sm">
                Test with REAL transactions ($1.00 each). These will actually process and appear in your payment processor dashboard.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Alert className="bg-red-100 border-red-300 mb-4">
            <AlertDescription className="text-red-900">
              <strong>⚠️ WARNING:</strong> Live mode will create REAL transactions that will be charged. 
              Only use this when you're ready to verify the complete end-to-end payment flow with actual money.
            </AlertDescription>
          </Alert>

          {liveTestResult && (
            <Alert className={liveTestResult.success ? "bg-green-100 border-green-300 mb-4" : "bg-red-100 border-red-300 mb-4"}>
              <AlertDescription className={liveTestResult.success ? "text-green-900" : "text-red-900"}>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    {liveTestResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <strong>{liveTestResult.success ? 'SUCCESS' : 'FAILED'}</strong>
                  </div>
                  <p><strong>Processor:</strong> {liveTestResult.processor?.toUpperCase()}</p>
                  <p><strong>Message:</strong> {liveTestResult.message}</p>
                  {liveTestResult.order_id && (
                    <p><strong>Order ID:</strong> {liveTestResult.order_id}</p>
                  )}
                  {liveTestResult.amount && (
                    <p><strong>Amount:</strong> ${liveTestResult.amount}</p>
                  )}
                  {liveTestResult.success && (
                    <p className="text-sm mt-2">
                      ✅ Check your {liveTestResult.processor} dashboard for order: <code className="bg-white px-2 py-1 rounded">{liveTestResult.order_id}</code>
                    </p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4 mb-4">
            {/* Card Details Input Form */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Enter Your Card Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Card Number
                  </label>
                  <input
                    type="text"
                    placeholder="4111 1111 1111 1111"
                    value={cardDetails.cardNumber}
                    onChange={(e) => setCardDetails({...cardDetails, cardNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    maxLength="19"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={cardDetails.cardName}
                    onChange={(e) => setCardDetails({...cardDetails, cardName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Month
                  </label>
                  <input
                    type="text"
                    placeholder="12"
                    value={cardDetails.expiryMonth}
                    onChange={(e) => setCardDetails({...cardDetails, expiryMonth: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    maxLength="2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Year
                  </label>
                  <input
                    type="text"
                    placeholder="25"
                    value={cardDetails.expiryYear}
                    onChange={(e) => setCardDetails({...cardDetails, expiryYear: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    maxLength="2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    placeholder="123"
                    value={cardDetails.cvv}
                    onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    maxLength="4"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Amount (Quality Assurance)
              </label>
              <select 
                value={liveMode ? 1.00 : 1.00}
                onChange={(e) => setLiveMode(parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value={1.00}>$1.00</option>
                <option value={2.00}>$2.00</option>
                <option value={3.00}>$3.00</option>
                <option value={4.00}>$4.00</option>
                <option value={5.00}>$5.00</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Select test amount for quality assurance testing</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-2xl">💳</span>
                </div>
                <div>
                  <h3 className="font-semibold">Stripe Live Test</h3>
                  <p className="text-sm text-gray-600">Process real ${typeof liveMode === 'number' ? liveMode.toFixed(2) : '1.00'} via Stripe</p>
                </div>
              </div>
              <Button
                onClick={() => runLiveModeTest('stripe', typeof liveMode === 'number' ? liveMode : 1.00)}
                disabled={liveTesting}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {liveTesting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Test Stripe (${typeof liveMode === 'number' ? liveMode.toFixed(2) : '1.00'} REAL)
                  </>
                )}
              </Button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-2xl">🔌</span>
                </div>
                <div>
                  <h3 className="font-semibold">PlugnPay Live Test</h3>
                  <p className="text-sm text-gray-600">Process real ${typeof liveMode === 'number' ? liveMode.toFixed(2) : '1.00'} via PlugnPay</p>
                </div>
              </div>
              <Button
                onClick={() => runLiveModeTest('plugnpay', typeof liveMode === 'number' ? liveMode : 1.00)}
                disabled={liveTesting}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {liveTesting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Test PlugnPay (${typeof liveMode === 'number' ? liveMode.toFixed(2) : '1.00'} REAL)
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Stripe Tests */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-2xl">💳</span>
              </div>
              <div>
                <CardTitle>Stripe Payment Tests</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {getProcessorSummary('stripe').tested}/{getProcessorSummary('stripe').total} tests completed
                  {getProcessorSummary('stripe').passed > 0 && ` • ${getProcessorSummary('stripe').passed} passed`}
                  {getProcessorSummary('stripe').failed > 0 && ` • ${getProcessorSummary('stripe').failed} failed`}
                </p>
              </div>
            </div>
            <Button
              onClick={() => runAllTests('stripe')}
              disabled={testing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Run All Stripe Tests
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {testFlows.map(flow => {
              const Icon = flow.icon;
              const result = testResults.stripe[flow.id];
              
              return (
                <div key={flow.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3 flex-1">
                    {getStatusIcon(result)}
                    <Icon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{flow.label}</div>
                      <div className="text-sm text-gray-500">{flow.description}</div>
                      {result && result !== 'testing' && !result.success && (
                        <div className="text-xs text-red-600 mt-1">
                          Error: {result.message}
                        </div>
                      )}
                      {result && result !== 'testing' && result.success && result.payment_url && (
                        <a 
                          href={result.payment_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                        >
                          View Payment URL →
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    {getStatusBadge(result)}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* PlugnPay Tests */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-2xl">⚡</span>
              </div>
              <div>
                <CardTitle>PlugnPay Payment Tests</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {getProcessorSummary('plugnpay').tested}/{getProcessorSummary('plugnpay').total} tests completed
                  {getProcessorSummary('plugnpay').passed > 0 && ` • ${getProcessorSummary('plugnpay').passed} passed`}
                  {getProcessorSummary('plugnpay').failed > 0 && ` • ${getProcessorSummary('plugnpay').failed} failed`}
                </p>
              </div>
            </div>
            <Button
              onClick={() => runAllTests('plugnpay')}
              disabled={testing}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Run All PlugnPay Tests
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {testFlows.map(flow => {
              const Icon = flow.icon;
              const result = testResults.plugnpay[flow.id];
              
              return (
                <div key={flow.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3 flex-1">
                    {getStatusIcon(result)}
                    <Icon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{flow.label}</div>
                      <div className="text-sm text-gray-500">{flow.description}</div>
                      {result && result !== 'testing' && !result.success && (
                        <div className="text-xs text-red-600 mt-1">
                          Error: {result.message}
                        </div>
                      )}
                      {result && result !== 'testing' && result.success && result.payment_url && (
                        <a 
                          href={result.payment_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                        >
                          View Payment URL →
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    {getStatusBadge(result)}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {(getProcessorSummary('stripe').tested > 0 || getProcessorSummary('plugnpay').tested > 0) && (
        <Card className="bg-gray-50">
          <CardHeader>
            <CardTitle>Test Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Stripe Results</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tests:</span>
                    <span className="font-medium">{getProcessorSummary('stripe').tested}/{getProcessorSummary('stripe').total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Passed:</span>
                    <span className="font-medium text-green-600">{getProcessorSummary('stripe').passed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-medium text-red-600">{getProcessorSummary('stripe').failed}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3">PlugnPay Results</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tests:</span>
                    <span className="font-medium">{getProcessorSummary('plugnpay').tested}/{getProcessorSummary('plugnpay').total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Passed:</span>
                    <span className="font-medium text-green-600">{getProcessorSummary('plugnpay').passed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-medium text-red-600">{getProcessorSummary('plugnpay').failed}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentTestingModule;
